import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Checkbox } from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { ColumnDef, CellContext, Switch, Tooltip } from '@shape-ds/components';
import { darkColors } from '@shape-ds/tokens';
import { severityMap, AgregNumber } from 'components';
import { SeverityPills, buildColumnsObject } from '@shape/lighthouse-components';
import { dateTimeDay, sortDatesDesc } from 'utils/DateUtil';
import { EVENT_PAGE } from 'config/paths';
import { ALARM_ASSIGNED_TYPE, alarmFieldType } from '../RelatedAlarmModalTable';
import { ASSIGNED_CHECKS_ENUM } from 'pages/Asset/tabs/Events/utils/EventGrouping';
import { getCssVar } from 'utils';
import { EventItemExtended } from 'queries/events/types';

export type EventItemColumns = EventItemExtended & { agreg?: string };
export type EventItemColumnsObject = {
  [key in string]: Partial<ColumnDef<EventItemColumns>>;
};

const useGetEventsColumns = (
  handleChange: ({
    changeField,
    id,
    value,
  }: {
    id: string;
    changeField: ALARM_ASSIGNED_TYPE;
    value: boolean;
  }) => void,
  disabled: boolean,
): EventItemColumnsObject => {
  const { t } = useTranslation();

  return {
    is_assigned_to_case: {
      size: 20,
      enableSorting: false,
      cell: (row: CellContext<EventItemColumns, unknown>) => {
        const { is_assigned_to_case, assignedChecks } = row.row.original;

        const isPartial = assignedChecks === ASSIGNED_CHECKS_ENUM.PARTIAL;

        return (
          <Checkbox
            indeterminate={isPartial}
            disabled={disabled}
            className="alarm-checkbox"
            value={is_assigned_to_case || isPartial}
            checked={is_assigned_to_case}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleChange({
                changeField: alarmFieldType.is_assigned_to_case,
                value: !is_assigned_to_case,
                id: row.row.original.id,
              });

              return false;
            }}
          />
        );
      },
    },
    severity: {
      size: 60,
      enableSorting: false,
      cell: (row: CellContext<EventItemColumns, unknown>) => {
        const severityLevel = row.row.original.severity;

        return <SeverityPills severity={severityMap[severityLevel]} />;
      },
    },
    alarm_datetime_start: {
      size: 80,
      enableSorting: false,
      sortingFn: (a: { original: EventItemExtended }, b: { original: EventItemExtended }) =>
        sortDatesDesc(a.original.alarm_datetime_start, b.original.alarm_datetime_start),
      cell: (row: CellContext<EventItemColumns, unknown>) => {
        return dateTimeDay(row.row.original.alarm_datetime_start);
      },
    },
    description: {
      enableSorting: false,
    },

    status: {
      size: 100,
      enableSorting: false,
      cell: (row: CellContext<EventItemColumns, unknown>) => {
        const { status } = row.row.original;

        if (!status) {
          return '-';
        }

        return (
          <div
            style={{
              color: status === 'OPEN' ? darkColors.RED_1 : darkColors.GREEN_1,
            }}
            className="case-related-status"
          >
            {status === 'OPEN' ? (
              <NotificationsActiveOutlinedIcon fontSize="small" />
            ) : (
              <CheckCircleOutlineOutlinedIcon fontSize="small" />
            )}
            {t(`event_page:status_count_panel.status.status.${status}`)}
          </div>
        );
      },
    },
    classification: {
      size: 65,
      enableSorting: false,
      cell: (row: CellContext<EventItemColumns, unknown>) => {
        if (!row.row.original.classification) {
          return '-';
        }
        return t(
          `event_page:status_count_panel.status.classification.${row.row.original.classification}`,
        );
      },
    },
    is_automatic: {
      size: 88,
      enableSorting: false,
      header: (props) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box>{t(`case:table.${props.column.id}`)}</Box>
          <Tooltip title={t('case:table.is_automatic_tooltip')} arrow>
            <InfoOutlinedIcon
              fontSize="small"
              sx={{
                marginLeft: '4px',
                color: getCssVar('--gray-300'),
                width: '16px',
                height: '16px',
              }}
            />
          </Tooltip>
        </Box>
      ),
      cell: (cell: CellContext<EventItemColumns, unknown>) => {
        const { id, is_automatic, is_assigned_to_case, is_automatic_blocked } = cell.row.original;

        const AutomaticWrapper = is_automatic_blocked ? Tooltip : 'div';

        return (
          <AutomaticWrapper
            title={is_automatic_blocked ? t('case:table.is_automatic_blocked_tooltip') : ''}
            followCursor={false}
            arrow
          >
            <div
              className="auto-attach-switch"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <Switch
                label=""
                checked={is_automatic}
                onChange={(e) => {
                  e.stopPropagation();
                  e.preventDefault();

                  handleChange({
                    changeField: alarmFieldType.is_automatic,
                    value: !is_automatic,
                    id: cell.row.parentId ?? id,
                  });
                }}
                disabled={is_assigned_to_case === false || is_automatic_blocked}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                    backgroundColor: is_automatic_blocked ? darkColors.GRAY_3 : darkColors.GRAY_4,
                    opacity: 1,
                  },
                }}
              />
              {is_automatic_blocked && (
                <LockOutlinedIcon
                  sx={{
                    color: darkColors.GRAY_6,
                    fontSize: 14,
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                  }}
                />
              )}
            </div>
          </AutomaticWrapper>
        );
      },
    },

    agreg: {
      size: 42,
      enableSorting: false,
      cell: (row: CellContext<EventItemColumns, unknown>) => (
        <AgregNumber value={row.row.original.children?.length} />
      ),
    },

    link: {
      size: 28,
      enableSorting: false,
      cell: (row: CellContext<EventItemColumns, unknown>) => {
        return (
          <Link to={EVENT_PAGE(row.row.original.id)} target="_blank" rel="noopener noreferrer">
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} color={darkColors.GRAY_6} />
          </Link>
        );
      },
    },
  };
};

export const useRelatedAlarmModalColumns = ({
  handleChange,
  disabled,
}: {
  handleChange: ({
    changeField,
    id,
    value,
  }: {
    id: string;
    changeField: ALARM_ASSIGNED_TYPE;
    value: boolean;
  }) => void;
  disabled: boolean;
}) => {
  const { t } = useTranslation();
  const eventsColumns = useGetEventsColumns(handleChange, disabled);
  return {
    columns: buildColumnsObject<EventItemColumns>({
      columns: eventsColumns,
      getLabel: (key) => t(`case:table.${key}`),
      overrideColumns: Object.keys(eventsColumns),
    }),
  };
};
